import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {
  memo,
  Ref,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { ProspectCellValue } from "../types";
import { formatScoreValue, formatSubmissionTime } from "./model";
import type { ExpandableProspectValueProps, ProspectCellProps } from "../types";

const getDisplayValue = (value: ProspectCellValue) => {
  return value === null || value === undefined || value === ""
    ? "-"
    : String(value);
};

const renderProspectValue = (
  value: ProspectCellValue,
  isExpanded = false,
  textRef?: Ref<HTMLSpanElement>,
) => {
  const displayValue = getDisplayValue(value);

  return (
    <Box
      component="span"
      ref={textRef}
      sx={{
        display: isExpanded ? "block" : "-webkit-box",
        overflow: isExpanded ? "visible" : "hidden",
        overflowWrap: "anywhere",
        textOverflow: isExpanded ? "clip" : "ellipsis",
        WebkitBoxOrient: "vertical",
        whiteSpace: isExpanded ? "pre-wrap" : "normal",
        width: "100%",
        ...(!isExpanded && { WebkitLineClamp: 3 }),
      }}
      title={displayValue}
    >
      {displayValue}
    </Box>
  );
};

const renderSubmissionTime = (value: ProspectCellValue) => {
  if (typeof value !== "number") {
    return renderProspectValue(value);
  }

  return renderProspectValue(formatSubmissionTime(value));
};

const ExpandableProspectValue = ({ value }: ExpandableProspectValueProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isNeedsExpanding, setIsNeedsExpanding] = useState(false);

  const updateTextOverflow = useCallback(() => {
    const text = textRef.current;
    if (!text) return;
    const toExpand =
      text.scrollHeight > text.clientHeight ||
      text.scrollWidth > text.clientWidth;
    setIsNeedsExpanding(toExpand);
  }, []);

  useLayoutEffect(() => {
    if (isExpanded) return;
    updateTextOverflow();
    const text = textRef.current;
    if (!text || typeof ResizeObserver === "undefined") return;
    const resizeObserver = new ResizeObserver(updateTextOverflow);
    resizeObserver.observe(text);
    return () => resizeObserver.disconnect();
  }, [isExpanded, updateTextOverflow, value]);

  return (
    <Box sx={{ width: "100%" }}>
      {renderProspectValue(value, isExpanded, textRef)}
      {(isExpanded || isNeedsExpanding) && (
        <Button
          onClick={(event) => {
            event.stopPropagation();
            setIsExpanded((currentIsExpanded) => !currentIsExpanded);
          }}
          size="small"
          sx={{
            lineHeight: 1.2,
            minWidth: 0,
            mt: 0.5,
            p: 0,
            textTransform: "none",
            textDecoration: "underline",
          }}
        >
          {isExpanded ? "Show less" : "Show more"}
        </Button>
      )}
    </Box>
  );
};

const ProspectCell = memo(function ProspectCell({
  value,
  scoreMax,
  isSubmissionTime,
  isExpandableText,
}: ProspectCellProps) {
  if (scoreMax !== undefined && typeof value === "number") {
    return renderProspectValue(formatScoreValue(value, scoreMax));
  }

  if (isSubmissionTime) {
    return renderSubmissionTime(value);
  }

  if (isExpandableText && typeof value === "string") {
    return <ExpandableProspectValue value={value} />;
  }

  return renderProspectValue(value);
});

export default ProspectCell;
